import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useBreakpointV2 } from "@tm/ui-widgets";
import { LandingHeaderMobile } from "../../components";
import { Page } from "./Claims.style";
import { ClaimsProvider } from "./ClaimsContext";
import { ClaimErrorBoundary } from "./components/ClaimErrorBoundary";
import { ClaimFormView } from "./components/ClaimFormView";
import { ClaimInfoView } from "./components/ClaimInfoView";
import { ClaimModalProvider } from "./modalManager";
import type {
  ApiComponentEntry,
  ClaimCardBlock,
  ClaimInfoViewParams,
  ClaimLandingNormalized,
  ClaimPageData,
  ClaimsWrapperProps,
  ClaimViewState,
} from "./types";
import { COMPONENT_ID_TO_CARD_NAME } from "./types";

const ClaimsDesktop = dynamic(() => import("./ClaimsDesktop"), { ssr: true });

const ClaimsPWA = dynamic(() => import("./ClaimsPWA"), {
  ssr: true,
});

function ClaimsWrapper({ data }: ClaimsWrapperProps) {
  const router = useRouter();
  const [claimData] = useState(data);
  const [activeView, setActiveView] = useState<ClaimViewState>("landing");
  const [infoViewParams, setInfoViewParams] =
    useState<ClaimInfoViewParams | null>(null);

  const refreshClaimData = useCallback(() => {
    if (typeof window !== "undefined") window.location.reload();
  }, []);

  const navigateToInfo = useCallback((params: ClaimInfoViewParams) => {
    setInfoViewParams(params);
    setActiveView("info");
  }, []);

  const navigateToLanding = useCallback(() => {
    setActiveView("landing");
    setInfoViewParams(null);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeView]);

  const normalizedData: ClaimLandingNormalized = useMemo(() => {
    const headingTitle = (claimData?.["headingTitle"] as string) ?? "";
    const pageData = (claimData?.["pageData"] as ClaimPageData) ?? {};

    const rawComponents =
      (claimData?.["components"] as ApiComponentEntry[]) ?? [];

    const cardList: ClaimCardBlock[] = rawComponents
      .filter(
        (c): c is ApiComponentEntry & { id: string } =>
          c != null && typeof c.id === "string"
      )
      .map((c) => ({
        cardName: COMPONENT_ID_TO_CARD_NAME[c.id] ?? c.id,
        data: c.data,
      }));

    const byCardName: Record<string, unknown> = {};
    for (const card of cardList) {
      if (card?.cardName) byCardName[card.cardName] = card?.data;
    }

    return { headingTitle, pageData, cardList, byCardName, raw: claimData };
  }, [claimData]);

  const ctxValue = useMemo(
    () => ({
      ...normalizedData,
      refreshClaimData,
      activeView,
      infoViewParams,
      navigateToInfo,
      navigateToLanding,
    }),
    [
      normalizedData,
      refreshClaimData,
      activeView,
      infoViewParams,
      navigateToInfo,
      navigateToLanding,
    ]
  );

  const device = useBreakpointV2();
  const isMobile = device === "sm";

  const queryBookingId =
    typeof router.query["bookingId"] === "string"
      ? router.query["bookingId"]
      : "";
  const partnerBookingId =
    typeof router.query["partnerBookingId"] === "string"
      ? router.query["partnerBookingId"]
      : undefined;
  const bookingId = normalizedData.pageData?.bookingId || queryBookingId;
  const currency = normalizedData.pageData?.currency || "INR";

  const mobileHeaderTitle =
    activeView === "info"
      ? infoViewParams?.categoryTitle?.trim() ?? ""
      : normalizedData.headingTitle?.trim() ?? "";

  const handleMobileBack =
    activeView === "info" ? navigateToLanding : undefined;

  const renderDetailView = () => {
    if (!infoViewParams) return null;
    const commonProps = {
      params: infoViewParams,
      bookingId,
      ...(partnerBookingId ? { partnerBookingId } : {}),
      currency,
      onBack: navigateToLanding,
    };
    return infoViewParams.contentType === "claimFlow" ? (
      <ClaimFormView {...commonProps} />
    ) : (
      <ClaimInfoView {...commonProps} />
    );
  };

  return (
    <ClaimsProvider value={ctxValue}>
      <ClaimModalProvider>
        <ClaimErrorBoundary onReset={navigateToLanding}>
          {isMobile ? (
            <Page>
              <LandingHeaderMobile
                displayWhiteHeader
                headerTitle={mobileHeaderTitle}
                handleBackBtn={handleMobileBack}
              />
              {activeView === "landing" && (
                <ClaimsPWA normalizedData={normalizedData} />
              )}
              {activeView === "info" && renderDetailView()}
            </Page>
          ) : (
            <>
              {activeView === "landing" && (
                <ClaimsDesktop normalizedData={normalizedData} />
              )}
              {activeView === "info" && renderDetailView()}
            </>
          )}
        </ClaimErrorBoundary>
      </ClaimModalProvider>
    </ClaimsProvider>
  );
}

export default ClaimsWrapper;
